import React, { useState, useEffect, useCallback } from "react";
import { showToast } from "../../utils/utils";
import { useParams } from "react-router-dom";
import { FaExclamationTriangle } from "react-icons/fa";

import Modalstyle from "./modal.module.scss";
import "./commentmodal.scss";
import PropTypes from "prop-types";
import { showToastType } from "../../utils/constants/appConstants";
import {
  buttonName,
  customersDtls,
  Inputfieldvalidation
} from "../../locales/en";
import privateAxios from "../../utils/axiosPrivate";
import {
  getPopupCommentsUrl,
  getVerifyPhoneNumberUrl
} from "../../utils/constants/apiEndPoints";
import { sanitizeInput } from "../../utils/utils";
import CommonModal from "./CommonModal";
import ErrorBoundary from "../error-boundary";
import ModalFallback from "../error-boundary/fallback/ModalFallback";

const CustomerCommentModal = ({ isOpen, toggle, sendData }) => {
  const { id } = useParams();
  const [comments, setComments] = useState([]);
  const [popupData, setPopUpData] = useState([]);
  const [additionalPopupData, setAdditionalPopupData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPopupRequired, setIsPopupRequired] = useState(true);
  const [commentError, setCommentError] = useState("");

  const handlePhoneVerifyChange = async () => {
    try {
      const response = await privateAxios.patch(getVerifyPhoneNumberUrl(id));

      if (response.status === 200) {
        showToast(response.data.message, showToastType.success);
        await fetchPopComments();
        toggle();
      }
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    }
  };

  const fetchPopComments = useCallback(async () => {
    try {
      const response = await privateAxios.get(getPopupCommentsUrl(id));
      if (response.status === 203) {
        setIsPopupRequired(false);
        setLoading(false);
        return;
      }
      const filteredComments = response?.data?.data?.comments.filter(
        (allComment) => allComment.deletedAt === null
      );
      setComments(filteredComments);
      setPopUpData(response?.data?.data);
      setAdditionalPopupData(response?.data?.data?.additionalPopupData);
      setLoading(false);
    } catch (error) {
      setCommentError(
        error?.response?.data?.message ||
          error?.response?.data?.errors ||
          Inputfieldvalidation.errorMsg.failedToLoadComments
      );
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetchPopComments();
  }, [isOpen, fetchPopComments]);

  const hasPrimary = !!sendData?.primaryContactNumber;
  const hasSecondary = !!sendData?.secondaryContactNumber;

  const primaryNotValidated =
    hasPrimary && sendData?.isPrimaryContactValidated === false;

  const secondaryNotValidated =
    hasSecondary && sendData?.isSecondaryContactValidated === false;

  const bothNotValidated = primaryNotValidated && secondaryNotValidated;
  const bothExist = hasPrimary && hasSecondary;

  const showPhoneValidationPopup = primaryNotValidated || secondaryNotValidated;

  const noPopupContent =
    (!popupData?.popupData || popupData.popupData.length === 0) &&
    (!additionalPopupData || additionalPopupData.length === 0) &&
    (!comments || comments.length === 0) &&
    !commentError &&
    !showPhoneValidationPopup;

  if (loading) {
    return null;
  }

  if (!isPopupRequired) {
    return null;
  }

  if (noPopupContent) return null;

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={toggle}
      title={customersDtls?.customerCmntModal.title}
      modalClassName={`modal-dialog-scrollable`}
      bodyClassName={Modalstyle.commentModalWrap}
      footerButtons={[
        {
          label: buttonName.close,
          className: "btn btn-secondary",
          onClick: toggle
        }
      ]}
    >
      <ErrorBoundary fallback={<ModalFallback fixHeight="300px" />}>
        <div className="row">
          <div className="popupData">
            {popupData?.popupData?.map((popup, index) => {
              return (
                <div
                  className="popupData_items col-12"
                  key={index}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeInput(popup.message)
                  }}
                />
              );
            })}
          </div>

          {commentError ? (
            <small className="mb-2">{commentError}</small>
          ) : (
            comments.length > 0 && (
              <div className="popUp_comments mb-4">
                <ul>
                  {comments.map((popup, index) => (
                    <li key={index} value={popup.value}>
                      {`${customersDtls?.customerCmntModal.comment} `}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: sanitizeInput(popup.comment)
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )
          )}

          {additionalPopupData.length > 0 && (
            <div className="popUp_comments mb-4">
              <ul className="additional-popup-data">
                {additionalPopupData.map((popup, index) => (
                  <li key={index}>
                    <span className="pe-2">
                      <FaExclamationTriangle />
                    </span>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: sanitizeInput(popup.message)
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {showPhoneValidationPopup && (
            <div className="mb-3 mt-1">
              <div className={Modalstyle.phoneValidationPopup}>
                <h3>{customersDtls?.customerCmntModal.validate}</h3>

                {bothExist && bothNotValidated ? (
                  <p className="both-validation">
                    {`${customersDtls?.customerCmntModal.thePrimary} `}
                    <strong>{sendData.primaryContactNumber}</strong>
                    {` ${customersDtls?.customerCmntModal.andSecondary} `}
                    <strong>{sendData.secondaryContactNumber}</strong>
                    {` ${customersDtls?.customerCmntModal.haveNot}`}
                  </p>
                ) : (
                  <>
                    {primaryNotValidated && (
                      <p className="primary-validation">
                        {`${customersDtls?.customerCmntModal.thePrimary} `}
                        <strong>{sendData.primaryContactNumber}</strong>
                        {` ${customersDtls?.customerCmntModal.hasNot}`}
                      </p>
                    )}

                    {secondaryNotValidated && (
                      <p className="secondary-validation">
                        {`${customersDtls?.customerCmntModal.theSecondary} `}
                        <strong>{sendData.secondaryContactNumber}</strong>
                        {` ${customersDtls?.customerCmntModal.hasNot2}`}
                      </p>
                    )}
                  </>
                )}

                <button
                  type="button"
                  onClick={handlePhoneVerifyChange}
                  className="btn btn-primary"
                >
                  {customersDtls?.customerCmntModal.forceBtn}
                </button>
              </div>
            </div>
          )}
        </div>
      </ErrorBoundary>
    </CommonModal>
  );
};
CustomerCommentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  sendData: PropTypes.object.isRequired
};

export default CustomerCommentModal;
